<template>
  <div class="block-wrapper data-receiving-config">
    <a-table
      :columns="columns" :data-source="rows" :pagination="false"
      bordered
    >
      <div slot="inputTitle" class="column-header">Параметры</div>
      <div slot="input" slot-scope="currentValue, { type }" class="column-input__container">
        <a-row type="flex" :gutter="10">
          <template v-if="type === formLines.SOURCE_TYPE">
            <a-col flex="2">
              <a-select v-model="data.data_receiving_source_type" @change="emitInput">
                <template v-for="item in sourceTypeList">
                  <a-select-option :key="item.value" :value="item.value">
                    {{ item.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-col>
          </template>

          <template v-else-if="type === formLines.FREQUENCY">
            <a-col flex="1">
              <a-select v-model="data.data_receiving_frequency" @change="emitInput">
                <template v-for="item in frequencyValues">
                  <a-select-option :key="item.value" :value="item.value">
                    {{ item.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-col>
            <a-col>
              <a-time-picker v-model="frequencyTimeMoment" format="HH:mm" @change="emitInput" />
            </a-col>
          </template>

          <template v-else-if="type === formLines.FILE_UPLOADING">
            <a-col flex="1">
              <dataset-uploader />
            </a-col>
          </template>

          <template v-else-if="type === formLines.LOADING_DATES">
            <a-col flex="1">
              <a-button type="primary">Загрузить</a-button>
            </a-col>
          </template>

          <template v-else-if="type === formLines.KKT_INFO_MIN">
            <a-col flex="1">
              <a-input-number
                v-model="data.data_receiving_kkt_info_min"
                :min="0"
                @change="emitInput"
              />
            </a-col>

            <a-col flex="1">
              <a-select v-model="data.data_receiving_kkt_info_min_unit" @change="emitInput">
                <template v-for="item in fileSizeUnitList">
                  <a-select-option :key="item.value" :value="item.value">
                    {{ item.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-col>
          </template>

          <template v-else-if="type === formLines.FISCAL_DATA_MIN">
            <a-col flex="1">
              <a-input-number
                v-model="data.data_receiving_fiscal_data_min"
                :min="0"
                @change="emitInput"
              />
            </a-col>

            <a-col flex="1">
              <a-select v-model="data.data_receiving_fiscal_data_min_unit" @change="emitInput">
                <template v-for="item in fileSizeUnitList">
                  <a-select-option :key="item.value" :value="item.value">
                    {{ item.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-col>
          </template>

          <template v-else-if="type === formLines.RELOADING_INTERVAL">
            <a-col flex="1">
              <a-input-number
                v-model="data.data_receiving_reloading_interval"
                :min="0"
                @change="emitInput"
              />
            </a-col>
          </template>

          <template v-else-if="type === formLines.LOADING_ATTEMPTS">
            <a-col flex="1">
              <a-input-number
                v-model="data.data_receiving_loading_attempts"
                :min="0"
                @change="emitInput"
              />
            </a-col>
          </template>

          <template v-else-if="type === formLines.NUMBER_OF_THREADS">
            <a-col flex="1">
              <a-input-number
                v-model="data.data_receiving_number_of_threads"
                :min="0"
                @change="emitInput"
              />
            </a-col>
          </template>

          <template v-else-if="type === formLines.DATE_INPUT">
            <a-col flex="2">
              <date-picker
                style="width: 100%; height: 32px;"
                :value="data.data_receiving_date_input"
                format="YYYY-MM-DD"
                class="white-date-picker"
                @input="data.data_receiving_date_input = $event"
                @change="emitInput"
              />
            </a-col>
            <a-col flex="1">
              <a-button type="primary" @click="uploadData">Начать загрузку</a-button>
            </a-col>
          </template>
        </a-row>
      </div>
    </a-table>
  </div>
</template>

<script>
import moment from "moment";
import DatasetUploader from '@/components/uploading/DatasetUploader';
import DatePicker from "@/components/base/DatePicker"
import {storage} from "@/helpers";
import store from "@/store";

const apiUrl = process.env.VUE_APP_ROSSTAT_API_URL;

const sourceTypes = Object.freeze({
  fns: {
    value: 'fns',
    name: 'ФНС',
  },
  external: {
    value: 'external',
    name: 'Внешний носитель',
  },
});

const frequencyValues = Object.freeze([
  {
    value: 1,
    name: 'Каждый день',
  },
  {
    value: 2,
    name: 'Каждые два дня',
  },
  {
    value: 7,
    name: 'Каждую неделю',
  },
]);

const fileSizeUnits = Object.freeze({
  MB: {
    value: 'mb',
    name: 'Мб',
  },
  GB: {
    value: 'gb',
    name: 'Гб',
  },
  TB: {
    value: 'tb',
    name: 'Тб',
  },
});

const formLines = Object.freeze({
  SOURCE_TYPE: 'sourceType',
  FREQUENCY: 'frequency',
  FILE_UPLOADING: 'fileUploading',
  LOADING_DATES: 'loadingDates',
  FREQUENCY_TIME: 'frequencyTime',
  KKT_INFO_MIN: 'kktInfoMin',
  KKT_INFO_MIN_UNIT: 'kktInfoMinUnit',
  FISCAL_DATA_MIN: 'fiscalDataMin',
  FISCAL_DATA_MIN_UNIT: 'fiscalDataMinUnit',
  RELOADING_INTERVAL: 'reloadingInterval',
  LOADING_ATTEMPTS: 'loadingAttempts',
});

const columns = Object.freeze([
  {
    title: 'Название',
    dataIndex: 'name',
    type: 'name',
    className: ['column-name'],
    width: '430px',
  },
  {
    type: 'input',
    scopedSlots: {
      customRender: 'input',
    },
    slots: {
      title: 'inputTitle',
    },
  },
]);

export default {
  name: 'DataReceivingConfig',
  components: { DatasetUploader, DatePicker },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    sourceTypes,
    frequencyValues,
    fileSizeUnits,
    formLines,
    columns,
    data: {
      data_receiving_source_type: sourceTypes.fns.value,
      data_receiving_frequency: frequencyValues[0].value,
      data_receiving_frequency_time: '00:00',
      data_receiving_kkt_info_min: 1024,
      data_receiving_kkt_info_min_unit: fileSizeUnits.GB.value,
      data_receiving_fiscal_data_min: 1,
      data_receiving_fiscal_data_min_unit: fileSizeUnits.TB.value,
      data_receiving_reloading_interval: 300,
      data_receiving_loading_attempts: 5,
      data_receiving_date_input: '',
      data_receiving_number_of_threads: 0,
    },
    dateFormat: 'YYYY-MM-DD',
  }),

  computed: {
    sourceTypeList: () => Object.keys(sourceTypes).map((key) => sourceTypes[key]),
    fileSizeUnitList: () => Object.keys(fileSizeUnits).map((key) => fileSizeUnits[key]),

    frequencyTimeMoment: {
      get() {
        return moment(this.data.data_receiving_frequency_time, 'HH:mm');
      },
      set(value) {
        this.data.data_receiving_frequency_time = value?.format('HH:mm') || '00:00';
      },
    },

    rows() {
      let schedulingRow =
        this.data.data_receiving_source_type === sourceTypes.fns.value
          ? [
              {
                type: formLines.DATE_INPUT,
                name: 'Загрузить данные',
              },
              {
                type: formLines.FREQUENCY,
                name: 'Периодичность',
              },
            ]
          : {
              type: formLines.FILE_UPLOADING,
              name: 'Загрузка файла',
            };
      if (!schedulingRow.length) {
        schedulingRow = [schedulingRow];
      }
      return [
        {
          type: formLines.SOURCE_TYPE,
          name: 'Источник альтернативных данных',
        },
        ...schedulingRow,
        {
          type: formLines.KKT_INFO_MIN,
          name: 'Минимальный размер kkt_info',
        },
        {
          type: formLines.FISCAL_DATA_MIN,
          name: 'Минимальный размер fiscal_data',
        },
        {
          type: formLines.RELOADING_INTERVAL,
          name: 'Интервал времени для повторной загрузки (секунд)',
        },
        {
          type: formLines.LOADING_ATTEMPTS,
          name: 'Количество попыток загрузки',
        },
        {
          type: formLines.NUMBER_OF_THREADS,
          name: 'Количество потоков',
        },
      ];
    },
  },

  watch: {
    value() {
      this.initData();
    },
  },

  mounted() {
    this.initData();
    this.emitInput();
  },

  methods: {
    moment,
    initData() {
      this.data = {
        ...this.data,
        ...this.value,
      };
    },

    emitInput() {
      this.$emit('input', this.data);
    },
    uploadData() {
      this.axios
        .post(`${apiUrl}/worker/start_job`, {
          pipeline: 'rosstat_fns_ftp_pipeline',
          run_config: {
            solids: {
              solid_fns_download: {
                config: {
                  period: this.data.data_receiving_date_input,
                  source: 'fns'
                },
              },
            },
          },
          mode: 'default',
        }, {
          headers: {
            'finch-auth': `Bearer ${storage.get('authToken')}`,
            'fio': encodeURIComponent(store.state.userFio),
            'region': encodeURIComponent(store.state.regionName)
          }
        })
        .then(() => {
          this.$notification.success({
            message: 'Загрузка началась. Смотрите журнал приема данных',
          });
        })
        .catch((err) => {
          console.log(err);
          this.$notification.error({
            message: 'Произошла ошибка. Обратитесь к администратору',
          });
        });
    },
  },
};
</script>

<style lang="scss">
.data-receiving-config {
  .column-name,
  .column-header {
    font-weight: bold;
  }

  .column-input {
    font-weight: normal;

    &__container {
      max-width: 400px;
    }
  }
}
</style>
